import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { restartableTask, dropTask } from 'ember-concurrency';
import { next } from '@ember/runloop';
import { guidFor } from '@ember/object/internals';
import { pluralize } from 'ember-inflector';
import moment from 'moment-timezone';
import getConfig from '../lib/get-config.js';
import { beebeeAjaxPost } from '../lib/utility/ajax.js';
import { formatEuro, currency2Number, number2Currency } from '../lib/utility/currency.js';
import { safeTimeZone } from '../lib/utility/dates.js';
import '../lib/utility/province.js';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{#if this.showButton}}\n  <button\n    type=\'button\'\n    class=\'{{@buttonClass}} {{if this.createPaymentTask.isRunning \"loading\"}}\'\n    data-toggle=\'{{if @useModal \"\" this.zfIntId}}\'\n    {{nv-disabled-reason this.noPermissionReason busy=this.createPaymentTask.isRunning}}\n    data-test-create-payment ...attributes {{on \'click\' (perform this.createPaymentTask)}}\n  >\n    {{#if @buttonIcon}}\n      <FaIcon @icon=\'{{@buttonIcon}}\' />\n    {{/if}}\n    <span class=\'description\'>\n      {{this.buttonText}}\n    </span>\n  </button>\n{{/if}}\n\n{{#if @useModal}}\n  <BbModalModern\n    @isOpen={{this.showModal}}\n    @title={{this.buttonText}}\n    @busy={{or\n      this.setAssuranceTask.isRunning\n      this.useCreditTask.isRunning\n      this.savePaymentTask.isRunning\n      this.reloadClientTask.isRunning\n    }}\n    @onClose={{fn (mut this.showModal) false}} data-test-set-show-modal\n  >\n    <:main>\n      {{#if this.newPayment}}\n        {{! form content identical to the ZfDropdown block }}\n        <div class=\'save-payments-modal-body\'>\n          <div class=\'nv-pf-grid grid-x grid-padding-x\'>\n            <div class=\'cell auto\'>\n              <span class=\'beebeeLabel\'>\n                {{t \'date\'}}\n              </span>\n              <BbInputDate\n                @originalDate={{this.newPayment.date}}\n                @enableTime={{true}}\n                @timeZone={{this.session.me.time_zone}}\n                @setDate={{fn (mut this.newPayment.date)}}\n              />\n            </div>\n            <div class=\'cell auto\'>\n              <span class=\'beebeeLabel\'>\n                {{t \'total\'}}\n              </span>\n              <BbInputCurrency\n                data-test-set-new-payment-total\n                @value={{this.newPayment.total}}\n                @action={{fn (mut this.newPayment.total)}}\n                @applicationController={{@applicationController}}\n              />\n            </div>\n          </div>\n\n          {{#if @item.assurance_id}}\n            <span class=\'label hollow\'>\n              {{t \'payment made by\'}}\n            </span>\n            <div class=\'grid-x grid-padding-x grid-padding-y\'>\n              <div class=\'cell auto\'>\n                <span class=\'beebeeLabel\'>\n                  {{t \'insurance supported\'}}\n                </span>\n                <button\n                  type=\'button\'\n                  class=\'button expanded\n                    {{this.buttonAssClass.disableAssistito}}\n                    {{if (is-equal this.newPayment.assurance.id @item.assurance_id) \"\" \"success\"}}\n                    {{if this.setAssuranceTask.isRunning \"loading\"}}\'\n                  disabled={{this.setAssuranceTask.isRunning}}\n                  data-test-set-assurance\n                  {{on \'click\' (fn (perform this.setAssuranceTask) false)}}\n                >\n                  {{@client.label}}\n                </button>\n                <div class=\'drop-title\'>\n                  <div class=\'sub-title\'>\n                    {{t \'insurance charge\'}}: {{format-euro @item.quota_assistito @item.valuta}}\n                  </div>\n                  <div class=\'sub-title\'>\n                    {{t \'appointment.paied\'}}: {{format-euro @item.pagato_assistito @item.valuta}}\n                  </div>\n                </div>\n              </div>\n              <div class=\'cell auto\'>\n                <span class=\'beebeeLabel\'>\n                  {{t \'insurance\'}}\n                </span>\n                <button\n                  type=\'button\'\n                  class=\'button expanded\n                    {{this.buttonAssClass.disableAssicurazione}}\n                    {{if (is-equal this.newPayment.assurance.id @item.assurance_id) \"success\"}}\n                    {{if this.setAssuranceTask.isRunning \"loading\"}}\'\n                  disabled={{this.setAssuranceTask.isRunning}}\n                  data-test-set-assurance-2\n                  {{on \'click\' (fn (perform this.setAssuranceTask) true)}}\n                >\n                  {{@item.assurance_name}}\n                </button>\n                <div class=\'drop-title\'>\n                  <div class=\'sub-title\'>\n                    {{t \'insurance charge\'}}: {{format-euro @item.quota_assicurazione @item.valuta}}\n                  </div>\n                  <div class=\'sub-title\'>\n                    {{t \'appointment.paied\'}}: {{format-euro\n                      @item.pagato_assicurazione\n                      @item.valuta\n                    }}\n                  </div>\n                </div>\n              </div>\n            </div>\n          {{/if}}\n\n          {{#if @client.credit}}\n            <span class=\'beebeeLabel\'>\n              {{t \'credit\'}}\n            </span>\n            {{#if this.reloadClientTask.isRunning}}\n              <BbSpinner />\n            {{else}}\n              <div class=\'grid-x grid-padding-x\'>\n                <div class=\'cell auto\'>\n                  <div class=\'text-right\'>\n                    {{format-euro @client.credit}}\n                  </div>\n                </div>\n                <div class=\'cell auto\'>\n                  <button\n                    type=\'button\'\n                    class=\'button expanded\n                      {{if this.useCredit \"success\"}}\n                      {{if this.useCreditTask.isRunning \"loading\"}}\'\n                    disabled={{this.useCreditTask.isRunning}}\n                    data-test-use-credit\n                    {{on \'click\' (perform this.useCreditTask)}}\n                  >\n                    {{t \'use credit\'}}\n                  </button>\n                </div>\n              </div>\n            {{/if}}\n          {{/if}}\n\n          {{#if this.isNuova}}\n            {{!-- M14 P1-P3 («UI nuova» only): sentence-case label, the method picker with a quiet «Cambia», the reason\n                 under it while no method is chosen. Classic markup below, unchanged. --}}\n            <div\n              class=\'nv-pmf {{if (and this.methodMissing this.methodAsked) \"nv-pmf--ask\"}}\'\n              id=\'nv-pmf-{{this.uid}}\'\n              role=\'group\'\n              aria-labelledby=\'nv-pmf-l-{{this.uid}}\'\n              data-test-nv-paid-by\n            >\n              <span class=\'nv-pmf-l\' id=\'nv-pmf-l-{{this.uid}}\'>{{t \'nv.payment.form.paid_by\'}}</span>\n              <BbPaymentsList\n                @payment_method={{this.newPayment.payment_method}}\n                @applicationController={{@applicationController}}\n                @nuova={{true}}\n                @onPick={{this.methodPicked}}\n              />\n              {{#if this.methodMissing}}\n                <p\n                  class=\'nv-pmf-msg\'\n                  id=\'nv-pmf-m-{{this.uid}}\'\n                  role={{if this.methodAsked \'alert\'}}\n                  data-test-nv-pay-method-missing={{if this.methodAsked \'asked\' \'hint\'}}\n                >\n                  <FaIcon @icon={{if this.methodAsked \'circle-xmark\' \'circle-info\'}} aria-hidden=\'true\' />\n                  {{t \'nv.payment.form.choose_method\'}}\n                </p>\n              {{/if}}\n            </div>\n          {{else}}\n            <span class=\'label hollow\'>\n              {{t \'paid by\'}}\n            </span>\n            <BbPaymentsList\n              @payment_method={{this.newPayment.payment_method}}\n              @applicationController={{@applicationController}}\n            />\n          {{/if}}\n        </div>\n      {{/if}}\n    </:main>\n    <:footer>\n      {{#if this.isNuova}}\n        <button\n          type=\'button\'\n          class=\'button primary expanded large\'\n          aria-busy={{if this.savePaymentTask.isRunning \'true\'}}\n          aria-disabled={{if this.methodMissing \'true\'}}\n          aria-describedby={{if this.methodMissing (concat \'nv-pmf-m-\' this.uid)}}\n          disabled={{this.savePaymentTask.isRunning}}\n          data-test-save-payment\n          {{on \'click\' this.saveNuova}}\n        >\n          {{t \'save\'}}\n        </button>\n      {{else}}\n        <button\n          type=\'button\'\n          class=\'button primary expanded large {{if this.savePaymentTask.isRunning \"loading\"}}\'\n          disabled={{this.savePaymentTask.isRunning}}\n          data-test-save-payment\n          {{on \'click\' (perform this.savePaymentTask)}}\n        >\n          {{t \'save\'}}\n        </button>\n      {{/if}}\n    </:footer>\n  </BbModalModern>\n{{/if}}\n\n{{#unless @useModal}}\n  <BbDropdown\n    @id=\'{{this.zfIntId}}\'\n    @onZfUiReady={{fn (mut this.zfDropdown)}}\n    @title={{this.buttonText}}\n    @notClose=\'true\' data-test-set-zf-dropdown\n  >\n    {{#if this.newPayment}}\n      <div class=\'nv-pf-grid grid-x grid-padding-x\'>\n        <div class=\'cell auto\'>\n          <span class=\'beebeeLabel\'>\n            {{t \'date\'}}\n          </span>\n          <BbInputDate\n            @originalDate={{this.newPayment.date}}\n            @enableTime={{true}}\n            @timeZone={{this.session.me.time_zone}}\n            @setDate={{fn (mut this.newPayment.date)}}\n          />\n        </div>\n        <div class=\'cell auto\'>\n          <span class=\'beebeeLabel\'>\n            {{t \'total\'}}\n          </span>\n\n          <BbInputCurrency\n            data-test-bb-payment-form-modal-set-new-payment-total\n            @value={{this.newPayment.total}}\n            @action={{fn (mut this.newPayment.total)}}\n            @applicationController={{@applicationController}}\n          />\n        </div>\n      </div>\n\n      {{#if @item.assurance_id}}\n        <span class=\'label hollow\'>\n          {{t \'payment made by\'}}\n        </span>\n\n        <div class=\'nv-pf-grid grid-x grid-padding-x grid-padding-y\'>\n          <div class=\'cell auto\'>\n            <span class=\'beebeeLabel\'>\n              {{t \'insurance supported\'}}\n            </span>\n            <button\n              type=\'button\'\n              class=\'button expanded\n                {{this.buttonAssClass.disableAssistito}}\n                {{if (is-equal this.newPayment.assurance.id @item.assurance_id) \"\" \"success\"}}\n                {{if this.setAssuranceTask.isRunning \"loading\"}}\'\n              disabled={{this.setAssuranceTask.isRunning}}\n              data-test-set-assurance-3\n              {{on \'click\' (fn (perform this.setAssuranceTask) false)}}\n            >\n              {{@client.label}}\n            </button>\n\n            <div class=\'drop-title\'>\n              <div class=\'sub-title\'>\n                {{t \'insurance charge\'}}: {{format-euro @item.quota_assistito @item.valuta}}\n              </div>\n              <div class=\'sub-title\'>\n                {{t \'appointment.paied\'}}: {{format-euro @item.pagato_assistito @item.valuta}}\n              </div>\n            </div>\n          </div>\n          <div class=\'cell auto\'>\n            <span class=\'beebeeLabel\'>\n              {{t \'insurance\'}}\n            </span>\n            <button\n              type=\'button\'\n              class=\'button expanded\n                {{this.buttonAssClass.disableAssicurazione}}\n                {{if (is-equal this.newPayment.assurance.id @item.assurance_id) \"success\"}}\n                {{if this.setAssuranceTask.isRunning \"loading\"}}\'\n              disabled={{this.setAssuranceTask.isRunning}}\n              data-test-set-assurance-4\n              {{on \'click\' (fn (perform this.setAssuranceTask) true)}}\n            >\n              {{@item.assurance_name}}\n            </button>\n\n            <div class=\'drop-title\'>\n              <div class=\'sub-title\'>\n                {{t \'insurance charge\'}}: {{format-euro @item.quota_assicurazione @item.valuta}}\n              </div>\n              <div class=\'sub-title\'>\n                {{t \'appointment.paied\'}}: {{format-euro @item.pagato_assicurazione @item.valuta}}\n              </div>\n            </div>\n          </div>\n        </div>\n      {{/if}}\n\n      {{#if @selectClients}}\n        <div class=\'nv-pf-grid grid-x grid-padding-x grid-padding-y\'>\n          <div class=\'cell auto\'>\n            <span class=\'beebeeLabel\'>\n              {{t \'client\'}}\n            </span>\n            <ul class=\'vertical menu action-sheet-scroll-box\'>\n              {{#each @selectClients as |sC|}}\n                <li>\n                  <a\n                    class=\'{{if (collection-contain sC this.newPaymentsContacts) \"active\"}}\'\n                    data-test-toggle-contact\n                    {{on \'click\' (fn (perform this.toggleContactTask) sC)}}\n                  >\n                    {{sC.label}}\n                  </a>\n                </li>\n              {{/each}}\n            </ul>\n          </div>\n        </div>\n      {{/if}}\n\n      {{#if @client.credit}}\n        <span class=\'beebeeLabel\'>\n          {{t \'credit\'}}\n        </span>\n\n        {{#if this.reloadClientTask.isRunning}}\n          <BbSpinner />\n        {{else}}\n          <div class=\'grid-x grid-padding-x\'>\n            <div class=\'cell auto\'>\n              <div class=\'text-right\'>\n                {{format-euro @client.credit}}\n              </div>\n            </div>\n            <div class=\'cell auto\'>\n              {{#if @client.credit}}\n                <button\n                  type=\'button\'\n                  class=\'button expanded\n                    {{if this.useCredit \"success\"}}\n                    {{if this.useCreditTask.isRunning \"loading\"}}\'\n                  disabled={{this.useCreditTask.isRunning}}\n                  data-test-use-credit-2\n                  {{on \'click\' (perform this.useCreditTask)}}\n                >\n                  {{t \'use credit\'}}\n                </button>\n              {{/if}}\n            </div>\n          </div>\n        {{/if}}\n      {{/if}}\n\n      {{#if @showResultInput}}\n        <BbField @label={{t \'result\'}}>\n          <BbRadio\n            @content={{this.newPayment.success}}\n            @setContent={{fn (mut this.newPayment.success)}}\n            @label={{t \'success\'}}\n            @value={{true}}\n            @name=\'success\'\n          />\n\n          <BbRadio\n            @content={{this.newPayment.success}}\n            @setContent={{fn (mut this.newPayment.success)}}\n            @value={{false}}\n            @name=\'refused\'\n            @label={{t \'refused\'}}\n          />\n        </BbField>\n      {{/if}}\n\n      {{#if this.newPayment.success}}\n        {{#if this.isNuova}}\n          {{!-- M14 P1-P3 («UI nuova» only): sentence-case label, the method picker with a quiet «Cambia», the reason\n               under it while no method is chosen. Classic markup below, unchanged. --}}\n          <div\n            class=\'nv-pmf {{if (and this.methodMissing this.methodAsked) \"nv-pmf--ask\"}}\'\n            id=\'nv-pmf-{{this.uid}}\'\n            role=\'group\'\n            aria-labelledby=\'nv-pmf-l-{{this.uid}}\'\n            data-test-nv-paid-by\n          >\n            <span class=\'nv-pmf-l\' id=\'nv-pmf-l-{{this.uid}}\'>{{t \'nv.payment.form.paid_by\'}}</span>\n            <BbPaymentsList\n              @payment_method={{this.newPayment.payment_method}}\n              @applicationController={{@applicationController}}\n              @nuova={{true}}\n              @onPick={{this.methodPicked}}\n            />\n            {{#if this.methodMissing}}\n              <p\n                class=\'nv-pmf-msg\'\n                id=\'nv-pmf-m-{{this.uid}}\'\n                role={{if this.methodAsked \'alert\'}}\n                data-test-nv-pay-method-missing={{if this.methodAsked \'asked\' \'hint\'}}\n              >\n                <FaIcon @icon={{if this.methodAsked \'circle-xmark\' \'circle-info\'}} aria-hidden=\'true\' />\n                {{t \'nv.payment.form.choose_method\'}}\n              </p>\n            {{/if}}\n          </div>\n        {{else}}\n          <span class=\'label hollow\'>\n            {{t \'paid by\'}}\n          </span>\n          <BbPaymentsList\n            @payment_method={{this.newPayment.payment_method}}\n            @applicationController={{@applicationController}}\n          />\n        {{/if}}\n\n        <p class=\'text-center\'>\n          <a data-test-show-more {{on \'click\' this.showMore}}>\n            <small>\n              {{#if this.showMoreNuova}}\n                {{! M11 W3 (J3): «fatture non pagata» did not agree; the new UI says what the link loads }}\n                {{this.showMoreNuova}}\n              {{else}}\n                {{@show}}\n                {{#if @expense}}\n                  {{this.session.me.organization.expenses_name}}\n                {{else}}\n                  {{this.session.me.organization.invoices_name}}\n                {{/if}}\n\n                {{t \'unpaied\'}}\n              {{/if}}\n            </small>\n          </a>\n        </p>\n      {{/if}}\n\n      <div class=\'action-sheet-scroll-box\'>\n        {{#each this.unpayedItems as |inv|}}\n          <div class=\'drop-title\'>\n            <div class=\'main-title\'>\n              {{inv.label}}\n            </div>\n            <div class=\'sub-title\'>\n              {{inv.sub_label}}\n            </div>\n          </div>\n\n          <button\n            type=\'button\'\n            class=\'button expanded {{if inv.selected \"success\"}}\'\n            data-test-add-invoice\n            {{on \'click\' (fn (perform this.addInvoiceTask) inv)}}\n          >\n            {{t \'pay now\'}}\n          </button>\n        {{/each}}\n      </div>\n\n      <div class=\'grid-x grid-padding-x\'>\n        <div class=\'cell shrink\'>\n          <span\n            role=\'button\'\n            class=\'button expanded large\'\n            data-test-bb-payment-form-modal-close\n            {{on \'click\' (fn this.zfDropdown.close this.zfDropdown.element)}}\n          >\n            {{t \'cancel\'}}\n          </span>\n        </div>\n        <div class=\'cell auto\'>\n          {{#if this.isNuova}}\n            <button\n              type=\'button\'\n              class=\'button primary expanded large\'\n              aria-busy={{if this.savePaymentTask.isRunning \'true\'}}\n              aria-disabled={{if this.methodMissing \'true\'}}\n              aria-describedby={{if this.methodMissing (concat \'nv-pmf-m-\' this.uid)}}\n              disabled={{this.savePaymentTask.isRunning}}\n              data-test-save-payment-2\n              {{on \'click\' this.saveNuova}}\n            >\n              {{t \'save\'}}\n            </button>\n          {{else}}\n            <button\n              type=\'button\'\n              class=\'button primary expanded large {{if this.savePaymentTask.isRunning \"loading\"}}\'\n              disabled={{this.savePaymentTask.isRunning}}\n              data-test-save-payment-2\n              {{on \'click\' (perform this.savePaymentTask)}}\n            >\n              {{t \'save\'}}\n            </button>\n          {{/if}}\n        </div>\n      </div>\n    {{/if}}\n  </BbDropdown>\n{{/unless}}");

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15;
let BbPaymentFormModalComponent = (_class = class BbPaymentFormModalComponent extends Component {
  // -----------------------------------------------------------------------
  // Local mutable state
  // -----------------------------------------------------------------------

  // Live reads of the host args. These were plain fields seeded ONCE in the
  // constructor (`this.event_ids = args.event_ids || []`), which froze them to
  // whatever was passed the moment the modal mounted. The host binds
  // `@event_ids={{this.eventCheckedId}}`, a getter that returns a fresh array as
  // the user checks more rows — so the constructor snapshot captured only the
  // first checked event and /pay_event fired with a single id no matter how many
  // were selected. Read the args live at use-time instead.
  get event_ids() {
    return this.args.event_ids || [];
  }
  get invoice_ids() {
    return this.args.invoice_ids || [];
  }
  get expense_ids() {
    return this.args.expense_ids || [];
  }

  // Backing field for `newPayment`: the setter has to republish the busy state
  // (see `_publishBusy`), so this cannot stay a plain `@tracked` field.

  get resolvedItem() {
    return this.args.item ?? this._resolvedItem;
  }
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "notify", _descriptor2, this);
    _initializerDefineProperty(this, "store", _descriptor3, this);
    _initializerDefineProperty(this, "session", _descriptor4, this);
    _initializerDefineProperty(this, "log", _descriptor5, this);
    // -----------------------------------------------------------------------
    // Stable per-instance ID (replaces Classic elementId)
    // -----------------------------------------------------------------------
    this.uid = guidFor(this);
    _initializerDefineProperty(this, "_newPayment", _descriptor6, this);
    // Highlights the use-credit button after credit has been applied
    _initializerDefineProperty(this, "useCredit", _descriptor7, this);
    _initializerDefineProperty(this, "newPaymentsContacts", _descriptor8, this);
    _initializerDefineProperty(this, "unpayedItems", _descriptor9, this);
    _initializerDefineProperty(this, "originalTotal", _descriptor0, this);
    _initializerDefineProperty(this, "savePaymentRunning", _descriptor1, this);
    _initializerDefineProperty(this, "isActive", _descriptor10, this);
    _initializerDefineProperty(this, "buttonAssClass", _descriptor11, this);
    // Captured from <BbDropdown @onZfUiReady={{fn (mut this.zfDropdown)}} …>
    _initializerDefineProperty(this, "zfDropdown", _descriptor12, this);
    // Modal state
    _initializerDefineProperty(this, "showModal", _descriptor13, this);
    // By-id mode (@item_id/@item_model passed WITHOUT @item — e.g. the group
    // roster's per-member pay button in event-group.hbs) has no item object: the
    // paid record is resolved by findRecord inside createPaymentTask. Persist it
    // here so the save / credit / assurance paths have a record to read. They all
    // used `this.args.item` directly, which is undefined in this mode, so the save
    // crashed (EMBER-MEDICAL-2V0) — and, once guarded, silently no-op'd instead of
    // saving. Read the item through `resolvedItem` everywhere past setup.
    _initializerDefineProperty(this, "_resolvedItem", _descriptor14, this);
    // -----------------------------------------------------------------------
    // Busy state published to the host through `@onBusyChange`
    // -----------------------------------------------------------------------
    //
    // Hosts gate this component behind a status check — invoice-detail's
    // `{{#if this.showPayment}}` (status === 'unpaied'), expense-detail's
    // `{{#unless this.isPaidAll}}`. The payment save resolves the invoice/expense
    // to PAID, so the moment the store learns that (the create response can
    // sideload the updated document) the gate closes, Glimmer tears this
    // component down mid-flight and ember-concurrency cancels the running task at
    // whatever `yield` it is sitting on. Everything after that point is silently
    // skipped: the /pay_event link, the local push onto `item.payments`, and the
    // trailing `safeReload()` — so the payment is on the server but never shows
    // up in the payments box until a full page refresh.
    //
    // `isBusy` is therefore held for the WHOLE lifetime of a draft (from
    // `createRecord` until the draft is cleared) plus every async operation, and
    // published to the host so it can keep the gate open until all of it — saves,
    // relation calls and model reloads — has finished.
    this._opCount = 0;
    this._busyPublished = false;
    // ── «UI nuova» payment method (M14 P1 / P2 / P3). Classic markup and behaviour unchanged (isNuova false). ──
    // P1: the studio's default method (option_field.default_payment_method, the same option the classic lists and
    // the ski-school payment drafts read) is preselected when it names one of the organization's methods; nothing
    // is preselected otherwise. While no method is chosen the form says so under «Pagato tramite» and «Salva» is
    // aria-disabled with that reason: a click focuses the picker instead of the 3-second «Seleziona metodo di
    // pagamento» toast. The save itself (savePaymentTask) is the classic one, and keeps its own check.
    _initializerDefineProperty(this, "methodAsked", _descriptor15, this);
    // M14 P1 review: the method we preset, and whether the user picked one in the list since (BbPaymentsList @onPick)
    this._presetMethod = null;
    this._methodPicked = false;
  }
  willDestroy() {
    // Un-saved payment record from an abandoned modal must not linger in the
    // store (it would show up in hasMany renders as a ghost row).
    if (this._newPayment?.isNew && !this._newPayment.isSaving) {
      this._newPayment.unloadRecord();
    }

    // Release the host's "keep me mounted" flag: this instance is going away,
    // so nothing may keep it latched (the flag lives on the HOST and outlives
    // us — a stale `true` would pin an add-payment CTA open forever).
    //
    // Deferred with `next()`: willDestroy runs inside the parent's render
    // transaction, and writing a tracked property the parent has already read
    // in that same transaction is a backtracking-rerender assertion. No target
    // is passed on purpose — a run-loop item targeting this (destroyed)
    // component would be dropped instead of released.
    this._opCount = 0;
    this._newPayment = null;
    if (this._busyPublished) {
      this._busyPublished = false;
      const onBusyChange = this.args.onBusyChange;
      if (onBusyChange) {
        next(() => onBusyChange(false));
      }
    }
    super.willDestroy();
  }
  get newPayment() {
    return this._newPayment;
  }
  set newPayment(value) {
    this._newPayment = value;
    this._publishBusy();
  }
  get isBusy() {
    return this._opCount > 0 || !!this._newPayment;
  }
  _beginOp() {
    this._opCount += 1;
    this._publishBusy();
  }
  _endOp() {
    this._opCount = Math.max(0, this._opCount - 1);
    this._publishBusy();
  }
  _publishBusy() {
    const busy = this.isBusy;
    if (busy === this._busyPublished) {
      return;
    }
    this._busyPublished = busy;
    this.args.onBusyChange?.(busy);
  }

  // -----------------------------------------------------------------------
  // Getters (replaced computed())
  // -----------------------------------------------------------------------

  get zfIntId() {
    return this.args.zfId || `${this.uid}-zf`;
  }

  // Classic default was `showButton: true` — preserve it (optional arg; most
  // callers rely on the trigger button showing by default).
  get showButton() {
    return this.args.showButton ?? true;
  }

  // A role without payment_insert pressed «Registra incasso», filled the whole form and only then read the
  // server's refusal: the trigger now says it up front. No role loaded yet → no verdict here.
  get noPermissionReason() {
    const role = this.session.me?.role;
    if (!role || role.payment_insert) return null;
    return this.intl.t('nv.reason.no_permission_payment');
  }
  get buttonText() {
    const w = this.args.wrapButtonText;
    return w === null || typeof w === 'undefined' ? this.intl.t('add payment') : w;
  }

  // -----------------------------------------------------------------------
  // Tasks
  // -----------------------------------------------------------------------

  *resolveRelationTask() {
    const evContacts = (yield this.newPayment?.contacts) || [];
    this.newPaymentsContacts = evContacts;
  }
  *createPaymentTask() {
    // `findRecord(..., { reload: true })` below can already flip the host's
    // status gate; hold the host open for the whole set-up too.
    this._beginOp();
    try {
      if (!this.session.me) {
        return;
      }
      const me = this.store.peekRecord('contact', this.session.me.id);
      if (!me) {
        return;
      }
      let item = this.args.item;
      if (this.args.item_id) {
        const loaded = yield this.store.findRecord(this.args.item_model, this.args.item_id, {
          reload: true
        });
        if (this.args.item) {
          // an item object WAS passed — sync the freshly loaded id onto it
          this.args.item.id = loaded.id;
        } else {
          // by-id path: no item object was passed, so `this.args.item` is
          // undefined — use the loaded record as the item instead of crashing
          // on `this.args.item.id =` / downstream `item.payment_due`
          // (Sentry EMBER-MEDICAL-2T0).
          item = loaded;
        }
      }

      // Persist the resolved record for the save/credit/assurance paths below
      // (by-id mode has no `this.args.item`).
      this._resolvedItem = item;
      let total = this.args.total ?? item.payment_due ?? 0;
      total = parseFloat(formatEuro(total).replace(/[.,]/g, '')) / 100;
      this.originalTotal = total;
      // args.useCredit is a local default; track it locally
      // (Classic set(this,'useCredit',false) — local field, not an arg)
      this.useCredit = false;
      let sent = !!this.args.expense;
      if (item.is_creditnote) {
        sent = !sent;
      }
      const newPayment = this.store.createRecord('payment', {
        sent,
        status: 'done',
        success: true,
        total,
        date: moment().tz(safeTimeZone(me.time_zone), true).format('YYYY-MM-DDTHH:mmZ')
      });
      const contacts = yield newPayment.contacts;
      contacts.push(me);
      if (this.args.client) {
        this.reloadClientTask.perform(contacts);
      }

      // M14 P1 («UI nuova» only): preselect the organization's default method, when it names one of its methods —
      // only for an INCOMING payment that is marked paid (review: never on an outgoing payment, expense / credit
      // note refund, where the studio default says nothing about how the money left).
      this.methodAsked = false;
      this._presetMethod = null;
      this._methodPicked = false;
      const preset = this.isNuova ? this.presetMethodFor(newPayment) : null;
      if (preset) {
        newPayment.payment_method = preset;
        this._presetMethod = preset;
      }
      this.newPayment = newPayment;

      // Trigger resolveRelationTask now that newPayment is set
      this.resolveRelationTask.perform();
      if (this.args.useModal) {
        this.showModal = true;
      }
      if (item.assurance_id) {
        const setAss = (item.quota_assistito || 0) - (item.pagato_assistito || 0);
        this.setAssuranceTask.perform(setAss <= 0);
      }
    } finally {
      // The draft, if one was created, keeps `isBusy` true on its own.
      this._endOp();
    }
  }
  *reloadClientTask(contacts) {
    const client = this.args.client;
    if (!client) {
      return;
    }
    const clientId = client.get ? client.id : client;
    if (!clientId) return;
    const contact = yield this.store.findRecord('contact', clientId, {
      reload: true
    });
    contacts.push(contact);
  }
  *unpayedItemTask() {
    const item = this.resolvedItem;
    const store = this.store;
    const contactId = item?.contact?.id;
    if (!contactId) return null;
    const model = this.args.expense ? 'expense' : 'invoice';
    const unpayedItems = yield store.query(model, {
      status: 'unpaied',
      document_type: 'invoice',
      contact_id: contactId
    });
    unpayedItems.forEach(itm => {
      if (itm.id === item.id) {
        itm.selected = true;
      }
    });
    this.unpayedItems = unpayedItems;
  }
  *setAssuranceTask(isAssicurazione) {
    const item = this.resolvedItem;
    if (!item) return;
    const quotaAssicurazione = item.quota_assicurazione || 0;
    const pagatoAssicurazione = item.pagato_assicurazione || 0;
    const quotaAssistito = item.quota_assistito || 0;
    const pagatoAssistito = item.pagato_assistito || 0;
    const dovutoAssicurazione = quotaAssicurazione - pagatoAssicurazione;
    const dovutoAssistito = quotaAssistito - pagatoAssistito;
    const dovuto = isAssicurazione ? dovutoAssicurazione : dovutoAssistito;
    this.buttonAssClass = {
      disableAssistito: dovutoAssistito <= 0 ? 'disabled' : '',
      disableAssicurazione: dovutoAssicurazione <= 0 ? 'disabled' : ''
    };
    if (dovuto <= 0) {
      this.notify.warning(this.intl.t('no payment due'));
      return;
    }
    if (isAssicurazione) {
      const assuranceId = item.assurance_id;
      if (!assuranceId) return;
      let assurance = this.store.peekRecord('assurance', assuranceId);
      if (!assurance) {
        assurance = yield this.store.findRecord('assurance', assuranceId, {
          reload: true
        });
      }
      this.newPayment.assurance = assurance;
    } else {
      this.newPayment.assurance = null;
    }
    this.newPayment.total = dovuto;
  }
  *useCreditTask() {
    const {
      store,
      session,
      zfDropdown
    } = this;
    const client = this.args.client;
    // resolvedItem covers by-id mode (group per-member pay); a still-null item
    // (teardown / never resolved) must not crash on `item.constructor`.
    const item = this.resolvedItem;
    if (!item) return false;
    const event_ids = this.event_ids;
    const invoice_ids = this.invoice_ids;
    if (!client?.credit) {
      return false;
    }
    const modelName = item.constructor.modelName;
    const itemId = item.id;
    const eventIds = Array.isArray(event_ids) ? [...event_ids] : [];
    const invoiceIds = Array.isArray(invoice_ids) ? [...invoice_ids] : [];
    if (modelName === 'event') {
      eventIds.push(itemId);
    } else if (modelName === 'invoice') {
      invoiceIds.push(itemId);
    }

    // `item.reload()` below settles the document, which closes the host's
    // status gate: hold it open until the whole sequence has finished.
    this._beginOp();
    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/use_credit`, session.token, {
        event_id: eventIds,
        invoice_id: invoiceIds,
        contact_id: client.id
      });
      yield item.reload();

      // Template highlights the button via this.useCredit (was written to a
      // dead underscore field: the success state never showed).
      this.useCredit = true;
      zfDropdown?.close();
      if (this.args.useModal) {
        this.showModal = false;
      }
    } catch (e) {
      this.notify.error(this.intl.t('generic error'));
    } finally {
      this._endOp();
    }
  }
  *savePaymentTask() {
    // Held for the ENTIRE save — relation wiring, POST /payments, POST
    // /pay_event, the local push onto `item.payments` and the trailing
    // `safeReload()` — and only released in the `finally`. Without it the host
    // gate closes as soon as the store sees the document settled and this
    // component is destroyed mid-task, so every step after that yield is
    // silently dropped (see the note on `isBusy`).
    this._beginOp();
    try {
      // `yield*` (delegation, NOT `yield`): the inner yields have to reach the
      // ember-concurrency runner. A bare `yield` of a generator object would
      // resolve it as an opaque non-thenable value and skip the whole body.
      return yield* this._savePaymentSequence();
    } finally {
      this._endOp();
    }
  }
  *_savePaymentSequence() {
    const {
      newPayment,
      store
    } = this;
    // resolvedItem covers by-id mode (group per-member pay button passes
    // @item_id/@item_model, not @item). A still-null item means either a
    // teardown race (host nulled @item mid-task) or the record never resolved —
    // nothing has been mutated yet, so bail cleanly instead of crashing on
    // `item.constructor` (Sentry EMBER-MEDICAL-2V0).
    const item = this.resolvedItem;
    const intl = this.intl;
    if (!item) return false;
    const modelName = item.constructor.modelName;
    if (newPayment.success && !newPayment.payment_method) {
      this.notify.warning(intl.t('select payment method'));
      return false;
    }
    if (newPayment.total) {
      newPayment.total = currency2Number(newPayment.total);
    }
    const total = parseFloat(newPayment.total);
    if (!total || isNaN(total) || total === 0) {
      this.notify.warning(intl.t('insert amount'));
      return false;
    }
    if (modelName) {
      const modelNamePlural = pluralize(modelName);
      const itemRecord = store.peekRecord(modelName, item.id);
      if (itemRecord) {
        const relation = yield newPayment.get(modelNamePlural);
        relation.push(itemRecord);
      }
      const relatedModels = ['invoice', 'expense', 'project', 'product', 'contact', 'event'];
      for (const name of relatedModels) {
        if (!this.args[`no_${name}`]) {
          const plural = pluralize(name);
          const itemId = item.get(`${name}.id`);
          const itemRecords = item[plural];
          const paymentRelation = yield newPayment.get(plural);
          if (itemId) {
            const relatedRecord = store.peekRecord(name, itemId);
            if (relatedRecord) {
              paymentRelation.push(relatedRecord);
            }
          }
          if (Array.isArray(itemRecords)) {
            for (const record of itemRecords) {
              paymentRelation.push(record);
            }
          }
        }
      }
    }

    // Add the owner user as a contact
    const userId = item.user?.id;
    if (userId) {
      const userRecord = store.peekRecord('contact', userId);
      if (userRecord) {
        const contacts = yield newPayment.get('contacts');
        contacts.push(userRecord);
      }
    }
    yield this._savePayment.perform();
  }
  *_savePayment() {
    this.savePaymentRunning = true;
    // Set once the record is on the server: the draft reference is only torn
    // down in the `finally` below, so the form body survives the whole tail.
    let committed = false;
    try {
      // `this.newPayment` is always a brand-new createRecord('payment') draft.
      const wasNew = this.newPayment?.isNew;
      const newPayment = yield this.args.applicationController.saveRecord(this.newPayment);

      // saveRecord swallows the rejection and resolves `false` after rolling the
      // record back, having already notified the user. For a new record that
      // rollback UNLOADS it from the store, so the template's
      // `{{#if this.newPayment}}` block would keep rendering a destroyed record
      // (`{{this.newPayment.total}}` etc.) and loop Glimmer until the tab OOMs.
      // The draft is unrecoverable once unloaded, so clear the reference and
      // close the form instead of leaving it bound to a dead record.
      if (newPayment === false) {
        if (wasNew) {
          this.newPayment = null;
          this.isActive = false;
          this.showModal = false;
        }
        return;
      }

      // NOT cleared here on purpose. The whole form body is rendered inside
      // `{{#if this.newPayment}}`, and the sequence below still has to run
      // /pay_event, push the payment onto `item.payments` and reload the model.
      // Dropping the reference at this point emptied the panel the moment the
      // POST answered: the modal stayed open (the busy flag holds it) but with
      // no content at all, for as long as the tail took. The draft is released
      // in the `finally`, so the body stays up with the save button in its
      // loading state until every request has settled.
      committed = true;

      // Post-commit tail: the payment is already saved. The item may be absent
      // (by-id group mode never had @item) or torn down during the POST (Sentry
      // EMBER-MEDICAL-2V0 class), so optional-chain the derefs — the pay_event
      // call below still runs for any pre-selected event/invoice/expense ids;
      // only the item-specific push is skipped, which is moot without an item.
      const item = this.resolvedItem;
      const modelName = item?.constructor?.modelName;
      const itemId = item?.id;
      const eventIds = Array.isArray(this.event_ids) ? [...this.event_ids] : [];
      if (modelName === 'event') {
        eventIds.push(itemId);
      }
      const invoiceIds = Array.isArray(this.invoice_ids) ? [...this.invoice_ids] : [];
      if (modelName === 'invoice') {
        invoiceIds.push(itemId);
      }
      const expenseIds = Array.isArray(this.expense_ids) ? [...this.expense_ids] : [];
      if (modelName === 'expense') {
        expenseIds.push(itemId);
      }
      const relatedInvoices = yield newPayment.get('invoices');
      const relatedInvoiceIds = relatedInvoices.map(inv => inv.id);
      const mergedInvoiceIds = [...new Set([...invoiceIds, ...relatedInvoiceIds])];
      const mergedEventIds = [...new Set(eventIds)];
      const mergedExpenseIds = [...new Set(expenseIds)];
      if ((mergedEventIds.length || mergedInvoiceIds.length || mergedExpenseIds.length) && newPayment.id) {
        try {
          yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/pay_event`, this.session.token, {
            event_id: mergedEventIds,
            invoice_id: mergedInvoiceIds,
            expense_id: mergedExpenseIds,
            payment_id: newPayment.id
          });
        } catch (e) {
          // the payment exists but is not linked: the item still looked unpaid and was charged twice
          this.log.error('Error during pay_event:', e);
          this.notify.warning(this.intl.t('nv.payment.link_failed'));
        }
      }

      // The related hasMany (e.g. invoice.payments) is declared with
      // `inverse: null`, so Ember Data does not wire the freshly saved payment
      // onto `item.payments` automatically. Push it in explicitly (deduped) so
      // the payments list updates without a full page refresh; the reload below
      // then reconciles against the backend's canonical set.
      try {
        const paymentsRel = item?.payments;
        if (paymentsRel && typeof paymentsRel.push === 'function' && !paymentsRel.find(p => p?.id === newPayment.id)) {
          paymentsRel.push(newPayment);
        }
      } catch (e) {
        this.log.error('Error pushing payment onto item.payments:', e);
      }
      if (typeof this.args.actionAfterSave === 'function') {
        this.args.actionAfterSave(newPayment);
      }
      yield this._reloadModel.perform();
      this.zfDropdown?.close();
      if (this.args.useModal) {
        this.showModal = false;
      }
    } catch (e) {
      this.notify.error(this.intl.t('generic error'));
    } finally {
      this.savePaymentRunning = false;

      // Release the draft only now — in the `finally`, so a throw in the tail
      // cannot leave the form bound to an already-saved record (which would
      // also pin `isBusy` true and keep the host's gate open forever).
      // Guarded on `committed`: a REFUSED save must keep whatever the user
      // typed, which is what the `newPayment === false` branch above decides.
      if (committed) {
        this.newPayment = null;
        this.isActive = false;
      }
    }
  }
  *_reloadModel() {
    const item = this.resolvedItem;
    if (item) {
      return yield item.safeReload();
    }
    return;
  }

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  /**
   * `payment.contacts` is declared `@hasMany('contact', { async: true })`, so
   * reading it gives a PromiseManyArray — an object whose whole surface is
   * `length / [] / forEach / reload / then / catch / finally`. It has no
   * `find`, no `indexOf`, no `push` and no `splice`, so the previous plain
   * action threw `contacts.find is not a function` on the very first click and
   * the selected client was never recorded on the payment.
   *
   * Resolving the relationship first yields the real ManyArray, which does
   * support those. The removal also has to use the index of the record we
   * FOUND: the row is rendered from `@selectClients`, whose records are not
   * guaranteed to be the same object identity as the ones on the relationship,
   * and `indexOf(client)` would then return -1 — deselecting in the UI while
   * leaving the contact attached to the payment.
   */
  *toggleContactTask(client) {
    if (!client || !this.newPayment) {
      return;
    }
    const contacts = yield this.newPayment.contacts;
    if (!contacts) {
      return;
    }
    const existing = contacts.find(contact => contact.id === client.id);
    if (existing) {
      const _i = contacts.indexOf(existing);
      if (_i !== -1) {
        contacts.splice(_i, 1);
      }
    } else {
      contacts.push(client);
    }

    // Keep resolveRelationTask in sync when contacts list changes
    yield this.resolveRelationTask.perform();
  }
  methodPicked() {
    this._methodPicked = true;
  }
  get isNuova() {
    try {
      return Boolean(getOwner(this)?.lookup?.('service:ui')?.isNuova);
    } catch {
      return false;
    }
  }
  get methodMissing() {
    const p = this.newPayment;
    return Boolean(p && p.success && !p.payment_method);
  }

  /** The organization's default method, by name (case-insensitive), or null. */
  nuovaDefaultMethod() {
    const wanted = this.session.option_field?.default_payment_method;
    if (!wanted || typeof wanted !== 'string') return null;
    let methods = [];
    try {
      methods = Array.from(this.session.payment_methods || []);
    } catch {
      methods = [];
    }
    const hit = methods.find(pm => String(pm?.name ?? '').toLowerCase() === wanted.trim().toLowerCase());
    return hit ? hit.name : null;
  }

  /** The method to preset on a new payment: the default, for an incoming paid payment only; else null. */
  presetMethodFor(payment) {
    if (!payment || payment.sent || !payment.success) return null;
    return this.nuovaDefaultMethod();
  }
  saveNuova() {
    // M14 P1 review: a payment switched to not paid («Esito: rifiutato») must not carry a method the user never
    // chose — only our preset; a method the user picked stays, exactly as classic saves it.
    const p = this.newPayment;
    if (p && !p.success && !this._methodPicked && this._presetMethod && p.payment_method === this._presetMethod) {
      p.payment_method = null;
    }
    if (this.methodMissing) {
      this.methodAsked = true;
      const group = typeof document !== 'undefined' ? document.getElementById(`nv-pmf-${this.uid}`) : null;
      group?.querySelector?.('button')?.focus?.();
      return;
    }
    this.savePaymentTask.perform();
  }

  // «UI nuova» label of the show-more link (M11 W3, finding J3). The classic text concatenates the plural
  // document name with the singular 'unpaied' («fatture non pagata»); the new UI names what unpayedItemTask
  // loads (the client's other unpaid documents). null outside the new UI: classic markup unchanged.
  get showMoreNuova() {
    let ui = null;
    try {
      ui = getOwner(this)?.lookup?.('service:ui');
    } catch {
      ui = null;
    }
    if (!ui?.isNuova) return null;
    if (this.args.expense) return this.intl.t('nv.payment.form.show_unpaid_expenses');
    // clinic-only-ok: «paziente» only when the app is the clinic one (getConfig isClinic)
    const clinic = Boolean(getConfig(this)?.APP?.isClinic);
    return this.intl.t(clinic ? 'nv.payment.form.show_unpaid_invoices_clinic' : 'nv.payment.form.show_unpaid_invoices');
  }
  showMore() {
    this.unpayedItemTask.perform();
  }

  /**
   * Same PromiseManyArray problem as toggleContactTask: `payment.invoices` and
   * `payment.expenses` are async hasMany, so the plain action threw
   * `collection.indexOf is not a function` and no extra document was ever
   * attached to the payment — the amount silently stayed at the original total.
   */
  *addInvoiceTask(item) {
    if (!item || !this.newPayment) {
      return;
    }
    const model = this.args.expense ? 'expenses' : 'invoices';
    // What the document still owes, not its full total: the list is every
    // 'unpaied' document of the client, which includes the partially paid ones,
    // and the payment opens on the host's own `payment_due` too. Adding the
    // total overcharged the payment by whatever was already paid. The amount
    // actually added is remembered, so deselecting takes back exactly that.
    // currency2Number: amounts can circulate as IT-format strings ("10,50")
    // and parseFloat would truncate at the comma.
    const totToAdd = item.selected ? this._addedDocumentAmounts().get(item) ?? this._amountDue(item) : this._amountDue(item);
    let totPayment = this.newPayment.total;
    if (typeof totPayment === 'string') {
      totPayment = currency2Number(totPayment);
    } else {
      totPayment = parseFloat(totPayment);
    }
    const collection = yield this.newPayment[model];
    if (collection) {
      if (item.selected) {
        const _i = collection.indexOf(item);
        if (_i !== -1) {
          collection.splice(_i, 1);
        }
        item.selected = false;
        this._addedDocumentAmounts().delete(item);
        this.newPayment.total = number2Currency(totPayment - totToAdd);
      } else {
        collection.push(item);
        item.selected = true;
        this._addedDocumentAmounts().set(item, totToAdd);
        this.newPayment.total = number2Currency(totPayment + totToAdd);
      }
    }
  }

  // Amount added to the payment per extra document, keyed by record. Created
  // lazily: the unit tests drive this class through Object.create(prototype),
  // where class fields never run.
  _addedDocumentAmounts() {
    if (!this.__addedDocumentAmounts) {
      this.__addedDocumentAmounts = new WeakMap();
    }
    return this.__addedDocumentAmounts;
  }

  // Residual of an invoice/expense (both models expose `payment_due` =
  // total - total_payments; the invoice honours fixed_price). Falls back to
  // the total only when the residual is unavailable.
  _amountDue(item) {
    const due = currency2Number(item.payment_due);
    if (typeof due === 'number' && !isNaN(due)) {
      return due;
    }
    return currency2Number(item.total) || 0;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "notify", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "store", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "session", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "log", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "_newPayment", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "useCredit", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "newPaymentsContacts", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "unpayedItems", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "originalTotal", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "savePaymentRunning", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "isActive", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "buttonAssClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "zfDropdown", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "showModal", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "_resolvedItem", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "resolveRelationTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "resolveRelationTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createPaymentTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "createPaymentTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reloadClientTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "reloadClientTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unpayedItemTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "unpayedItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setAssuranceTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "setAssuranceTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "useCreditTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "useCreditTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "savePaymentTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "savePaymentTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_savePayment", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "_savePayment"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_reloadModel", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "_reloadModel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleContactTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "toggleContactTask"), _class.prototype), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "methodAsked", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "methodPicked", [action], Object.getOwnPropertyDescriptor(_class.prototype, "methodPicked"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "saveNuova", [action], Object.getOwnPropertyDescriptor(_class.prototype, "saveNuova"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "showMore", [action], Object.getOwnPropertyDescriptor(_class.prototype, "showMore"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addInvoiceTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "addInvoiceTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbPaymentFormModalComponent);

export { BbPaymentFormModalComponent as default };
//# sourceMappingURL=bb-payment-form-modal.js.map
